import {
  LayoutDashboard,
  Database,
  Workflow,
  Users,
  Code2,
  PanelLeftClose,
  PanelLeftOpen,
  CheckSquare,
  User,
  LogOut,
  Phone,
  PhoneCall,
  Headset,
  Settings,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import type { ReactElement } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip'
import { NotificationMenu } from '@/features/notification'
import { useAuth } from '@/hooks/useAuth'
import { useCallingEnabled } from '@/hooks/useCalling'
import { usePermission } from '@/hooks/usePermission'
import { useAuthStore } from '@/stores/auth.store'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import { cn } from '@/utils/cn'

import { WorkspaceSwitcher } from './WorkspaceSwitcher'


interface NavItem {
  label: string
  href: string
  icon: React.ElementType
  /** Shown when the user holds this permission on at least one resource. */
  permission?: keyof typeof PERMISSION_KEYS
  /** Shown when the user holds any of these (alternative to `permission`). */
  anyPermission?: (keyof typeof PERMISSION_KEYS)[]
  /** Shown only while calling is available (deployment switch and plan, see useCallingEnabled). */
  requiresCalling?: boolean
  developerOnly?: boolean
}

const navItems: NavItem[] = [
  {
    label: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
  },
  {
    label: 'Record Groups',
    href: '/record-groups',
    icon: Database,
    permission: 'RECORD_GROUP_VIEW',
  },
  {
    label: 'Tasks',
    href: '/tasks',
    icon: CheckSquare,
  },
  {
    label: 'Automations',
    href: '/automations',
    icon: Workflow,
    permission: 'AUTOMATION_VIEW',
  },
  {
    label: 'Calls',
    href: '/calls',
    icon: PhoneCall,
    anyPermission: ['CALL_MAKE', 'CALL_VIEW_ALL'],
    requiresCalling: true,
  },
  {
    label: 'My phone',
    href: '/calling/my-phone',
    icon: Phone,
    permission: 'CALL_MAKE',
    requiresCalling: true,
  },
  {
    label: 'Telephony',
    href: '/calling/settings',
    icon: Headset,
    permission: 'CALL_CONFIGURE',
    requiresCalling: true,
  },
  {
    label: 'Settings',
    href: '/settings',
    icon: Settings,
    // Users, roles, billing, trash and the audit log are tabs of one page; each checks its own permission.
    // No gate here: every member manages their own mailboxes in its Mailboxes tab.
  },
]

const developerNavItems: NavItem[] = [
  {
    label: 'Developer Dashboard',
    href: '/developer',
    icon: Code2,
    developerOnly: true,
  },
  {
    label: 'Workspaces',
    href: '/developer/workspaces',
    icon: Database,
    developerOnly: true,
  },
  {
    label: 'Users',
    href: '/developer/users',
    icon: Users,
    developerOnly: true,
  },
  {
    label: 'Analytics',
    href: '/developer/analytics',
    icon: LayoutDashboard,
    developerOnly: true,
  },
]

export interface SidebarProps {
  isCollapsed?: boolean
  isMobileOpen?: boolean
  onToggleCollapse?: () => void
  onCloseMobileMenu?: () => void
  onOpenCreateModal?: () => void
}

interface UserDropdownProps {
  trigger: ReactElement
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  sideOffset?: number
}

export const UserDropdown = ({
  trigger,
  side,
  align,
  sideOffset,
}: UserDropdownProps) => {
  const { logout } = useAuth()
  const userEmail = useAuthStore((state) => state.email)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger}
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        className="w-56"
      >
        <div className="px-3 py-2">
          <p className="truncate text-sm font-medium text-neutral-900">
            {userEmail || 'User'}
          </p>
        </div>

        <DropdownMenuSeparator />

        <DropdownMenuItem className="cursor-pointer" asChild>
          <Link to="/account">
            <User className="mr-2 h-4 w-4" />
            My account
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem
          className="cursor-pointer text-red-600 focus:text-red-600"
          onClick={logout}
        >
          <LogOut className="mr-2 h-4 w-4" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function Sidebar({
  isCollapsed: controlledCollapsed,
  isMobileOpen = false,
  onToggleCollapse,
  onCloseMobileMenu,
  onOpenCreateModal,
}: SidebarProps = {}) {
  const [internalCollapsed, setInternalCollapsed] = useState(false)
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' &&
    window.matchMedia('(min-width: 1024px)').matches
  )

  const location = useLocation()
  const { canAnywhere, hasRecordGroupAccess, isDeveloper } = usePermission()
  const { enabled: callingEnabled } = useCallingEnabled()
  const userEmail = useAuthStore((state) => state.email)

  const isCollapsed =
    controlledCollapsed !== undefined
      ? controlledCollapsed
      : internalCollapsed

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)')
    const handleViewportChange = () => setIsDesktop(mediaQuery.matches)

    handleViewportChange()
    mediaQuery.addEventListener('change', handleViewportChange)
    return () => mediaQuery.removeEventListener('change', handleViewportChange)
  }, [])

  // A desktop collapse preference must never make the mobile sidebar narrow.
  const isSidebarCollapsed = isCollapsed && isDesktop

  const handleToggleCollapse =
    onToggleCollapse ??
    (() => setInternalCollapsed((prev) => !prev))

  const handleCloseMobileMenu = onCloseMobileMenu ?? (() => undefined)

  const isVisible = (item: NavItem) => {
    if (item.developerOnly) return isDeveloper
    if (item.requiresCalling && !callingEnabled) return false
    // Record groups are also reachable through content shared inside them (trace access).
    if (item.permission === 'RECORD_GROUP_VIEW' && hasRecordGroupAccess) return true
    if (item.anyPermission) return item.anyPermission.some((key) => canAnywhere(PERMISSION_KEYS[key]))
    return !item.permission || canAnywhere(PERMISSION_KEYS[item.permission])
  }

  const filteredNavItems = navItems.filter(isVisible)
  const filteredDeveloperNavItems = developerNavItems.filter(isVisible)

  const NavLink = ({ item }: { item: NavItem }) => {
    const isActive =
      location.pathname === item.href ||
      location.pathname.startsWith(`${item.href}/`)

    const Icon = item.icon

    const linkContent = (
      <Link
        to={item.href}
        className={cn(
          'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
          isActive
            ? 'bg-primary-100 text-primary-900'
            : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
        )}
        onClick={handleCloseMobileMenu}
      >
        <Icon className="h-5 w-5 shrink-0" />

        <span
          className={cn(
            'overflow-hidden whitespace-nowrap transition-all duration-300',
            isSidebarCollapsed
              ? 'max-w-0 opacity-0'
              : 'max-w-[200px] opacity-100'
          )}
        >
          {item.label}
        </span>
      </Link>
    )

    if (isSidebarCollapsed) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            {linkContent}
          </TooltipTrigger>

          <TooltipContent side="right">
            {item.label}
          </TooltipContent>
        </Tooltip>
      )
    }

    return linkContent
  }

  return (
    <TooltipProvider>
      <>
        {/* Mobile Overlay */}
        {isMobileOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={handleCloseMobileMenu}
          />
        )}

        {/* Sidebar */}
        <aside
          className={cn(
            'group fixed left-0 top-0 z-50 flex h-full w-64 flex-col border-r border-neutral-200 bg-white transition-all duration-300 ease-in-out',
            isSidebarCollapsed && 'lg:w-16',
            isMobileOpen
              ? 'translate-x-0'
              : '-translate-x-full lg:translate-x-0'
          )}
        >
          {/* Workspace Switcher */}
          <div
            className={cn(
              'relative flex h-16 shrink-0 items-center transition-all duration-300',
              isSidebarCollapsed
                ? 'justify-center px-2 lg:justify-center'
                : 'justify-between px-3'
            )}
          >
            <div
              className={cn(
                'min-w-0 flex-1',
                !isSidebarCollapsed && 'pr-5',
                isSidebarCollapsed && 'lg:flex-none'
              )}
            >
              <WorkspaceSwitcher
                isCollapsed={isSidebarCollapsed}
                onOpenCreateModal={() => {
                  handleCloseMobileMenu()
                  onOpenCreateModal?.()
                }}
                onWorkspaceSelect={handleCloseMobileMenu}
              />
            </div>

            {/* Collapse / Expand Button */}
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'absolute -right-3 top-1/2 z-30 hidden h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full border border-neutral-200 bg-white p-0 shadow-sm hover:bg-neutral-100 lg:flex'
              )}
              onClick={handleToggleCollapse}
            >
              {isSidebarCollapsed ? (
                <PanelLeftOpen
                  size={15}
                  strokeWidth={2}
                  className="text-neutral-600"
                />
              ) : (
                <PanelLeftClose
                  size={15}
                  strokeWidth={2}
                  className="text-neutral-600"
                />
              )}
            </Button>
          </div>

          {/* Divider */}
          <div className="shrink-0 border-t border-neutral-200" />

          {/* Scrollable Navigation */}
          <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2">
            {filteredNavItems.map((item) => (
              <NavLink
                key={item.href}
                item={item}
              />
            ))}

            {filteredDeveloperNavItems.length > 0 && (
              <div className="pt-4">
                <div
                  className={cn(
                    'overflow-hidden whitespace-nowrap px-3 py-2 text-xs font-semibold uppercase text-neutral-400 transition-all duration-300',
                    isSidebarCollapsed
                      ? 'max-h-0 py-0 opacity-0'
                      : 'max-h-8 opacity-100'
                  )}
                >
                  Developer
                </div>

                {filteredDeveloperNavItems.map((item) => (
                  <NavLink
                    key={item.href}
                    item={item}
                  />
                ))}
              </div>
            )}
          </nav>

          {/* Fixed Bottom Section */}
          <div className="shrink-0 border-t border-neutral-200 p-2">
            {/* Notifications */}
            {isSidebarCollapsed ? (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <div className="flex w-full justify-center">
                    <NotificationMenu
                      showLabel={false}
                      navigateToNotifications={!isDesktop}
                      triggerClassName="h-10 w-10 justify-center rounded-lg px-0"
                      
                    />
                  </div>
                </TooltipTrigger>

                <TooltipContent side="right">
                  Notifications
                </TooltipContent>
              </Tooltip>
            ) : (
              <NotificationMenu
                showLabel
                navigateToNotifications={!isDesktop}
                triggerClassName="h-10 w-full justify-start rounded-lg px-3"
              />
            )}

            {/* User Account */}
            <UserDropdown
              side="right"
              align="end"
              sideOffset={8}
              trigger={
                <button
                  type="button"
                  className={cn(
                    'mt-1 flex h-10 w-full items-center rounded-lg transition-colors hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                    isSidebarCollapsed
                      ? 'justify-center px-0'
                      : 'justify-start gap-3 px-3'
                  )}
                >
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-100 text-[10px] font-semibold text-primary-700">
                    {userEmail?.charAt(0).toUpperCase() || 'U'}
                  </div>

                  {!isSidebarCollapsed && (
                    <span className="min-w-0 flex-1 truncate text-left text-sm text-neutral-600">
                      {userEmail || 'User'}
                    </span>
                  )}
                </button>
              }
            />
          </div>
        </aside>
      </>
    </TooltipProvider>
  )
}
